import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Define the shape of a delete request (adjust if needed)
interface DeleteRequestData {
  id: number;
  addressId: number;
  userId: number;
  deleteReason: string;
  createdDate: string;
  deleteStatus: boolean;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  pinCode: string;
  blockCode: string;
  addressCode: string;
  latitude: string;
  longitude: string;
  userName: string;
  email: string;
  phoneNumber: number;
}

const DeleteRequestDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Expecting the delete request data to be passed via state as "deleteRequestData"
  const deleteRequest = location.state?.deleteRequestData as DeleteRequestData;

  if (!deleteRequest) {
    return (
      <div className="min-h-screen bg-magnolia p-6">
        <div className="bg-white rounded-lg p-6">
          <p className="text-red-600">No delete request data found.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-magnolia p-8">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl mx-auto backdrop-blur-sm bg-opacity-95">
        <h1 className="text-3xl font-bold mb-8 text-gray-800 border-b pb-4 flex items-center">
          <span className="bg-blue-600 w-2 h-8 rounded mr-3"></span>
          Delete Request Details
        </h1>

        <div className="space-y-8">
          {/* Grid with basic delete request info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* User Name */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
              <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                </svg>
                User Name
              </h2>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="text-blue-600 font-medium">
                  {deleteRequest.userName}
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
              <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                Email
              </h2>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="text-blue-600 font-medium">{deleteRequest.email}</p>
              </div>
            </div>

            {/* Phone Number */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
              <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2c-5.18 0-10-5.82-10-13V3z" />
                </svg>
                Phone Number
              </h2>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="text-blue-600 font-medium">
                  {deleteRequest.phoneNumber}
                </p>
              </div>
            </div>

            {/* Created Date */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md transform hover:scale-[1.02] transition-transform">
              <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM6 7a1 1 0 000 2h8a1 1 0 100-2H6z" />
                </svg>
                Created Date
              </h2>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="text-blue-600 font-medium">
                  {deleteRequest.createdDate}
                </p>
              </div>
            </div>
          </div>

          {/* Delete Reason */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md">
            <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M4 5h12v2H4z" />
              </svg>
              Delete Reason
            </h2>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <p className="text-blue-600 font-medium whitespace-pre-line break-words">
                {deleteRequest.deleteReason || 'No delete reason provided.'}
              </p>
            </div>
          </div>

          {/* Address */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md">
            <h2 className="font-semibold text-gray-700 mb-3 flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 2a6 6 0 016 6v3a6 6 0 11-12 0V8a6 6 0 016-6z" />
                <path d="M2 13a8 8 0 0116 0v3H2v-3z" />
              </svg>
              Address
            </h2>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <p className="text-blue-600 font-medium whitespace-pre-line break-words">
                {deleteRequest.addressLine1}
                <br />
                {deleteRequest.addressLine2 && (
                  <>
                    {deleteRequest.addressLine2}
                    <br />
                  </>
                )}
                {deleteRequest.addressLine3 && (
                  <>
                    {deleteRequest.addressLine3}
                    <br />
                  </>
                )}
                Pin Code: {deleteRequest.pinCode}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate(-1)}
          className="mt-8 bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Go Back
        </button>
      </div>
    </div>
  );
};

export default DeleteRequestDetails;
